import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CoreSeoPages } from '@app/store/enums/core-seo-pages';
import { IAppConfig } from '@app/store/models/config.interface';
import { Subscription } from 'rxjs';
import { SessionInfo } from '@app/store/models/session-info.interface';
import { APP_CONFIG } from '../../config/config';
import { CoreService } from '../../services/core.service';
import { FAQCategories } from '../../services/faqs.service';
import { FunctionsService } from '../../services/functions.service';
import { PaymentService } from '../../services/payment.service';
import { SeoService } from '../../services/seo.service';
import { LocalSubPricing } from '@app/store/models/local-sub-pricing.interface';
import { SubscriptionPriceOnCheckout } from '@app/store/models/subscription-price-on-checkout.interface';
import { selectAppState } from '@app/store/selectors/app.selectors';
import { Store } from '@ngrx/store';
import * as AppActions from '@app/store/actions/app.actions';
import { SubscriptionPriceOnCheckoutActions, ProductSubscriptionDurations, Pricings, ProductCodes, PublicUserDetails, SystemUserTypes } from 'shared-library';

declare const bootstrap: any

@Component({
  selector: 'app-core-subcription-pricing-page',
  templateUrl: './core-subcription-pricing-page.component.html',
  styleUrls: ['./core-subcription-pricing-page.component.scss']
})
export class CoreSubcriptionPricingPageComponent implements OnInit, OnDestroy {

  private prices: Pricings[] = []
  busy_ids: string[] = []
  faq_category = FAQCategories.PAYMENT
  local_prices: LocalSubPricing[] = null

  appState$ = this.store.select(selectAppState);

  downgrading = false
  selected_local_price: LocalSubPricing

  session_info: SessionInfo
  private subscriptions: Subscription[] = []
  @Input() user_details: PublicUserDetails = null;

  duration: ProductSubscriptionDurations = ProductSubscriptionDurations.YEAR
  durations = {
    yearly: ProductSubscriptionDurations.YEAR,
    monthly: ProductSubscriptionDurations.MONTH
  }

  modal_element: any

  constructor(
    private store: Store,
    private router: Router,
    private seoService: SeoService,
    public coreService: CoreService,
    public functionService: FunctionsService,
    public readonly paymentService: PaymentService,
    @Inject(APP_CONFIG) private readonly config: IAppConfig
  ) {
    this.subscriptions.push(this.coreService.session_dataObservable.subscribe(si => this.session_info = si))

    this.subscriptions.push(
      this.appState$.subscribe(async ls => {
        const length_before = this.prices.length
        this.prices = ls.data.cache.products.plans
        this.interpretPricing()
        if (!length_before && this.prices.length) {
          await this.privateCheckCallback()
        }
      })
    )
  }

  async ngOnInit() {
    if (this.coreService.isLoggedIn()) {
      if (!this.coreService.hasPermission(`${this.session_info.user_details.user_category}.USERMANAGEMENT.PAYMENT`)) {
        this.router.navigateByUrl("/403")
      }
      if (!this.user_details) this.user_details = JSON.parse(JSON.stringify(this.session_info.user_details))
      this.privateCheckCallback()
    }
    this.getPricings()
    this.seoService.generateMetaTags(CoreSeoPages.PRICING)
  }

  ngOnDestroy(): void {
    this.subscriptions.map(s => s.unsubscribe())
  }

  private async privateCheckCallback() {
    if (this.coreService.isLoggedIn() && this.user_details) {

      if (this.session_info.payment.selected_subscription_price && this.prices.length) {
        if (this.prices.find(price => price.id === this.session_info.payment.selected_subscription_price.id)) {
          await this.getStarted(this.session_info.payment.selected_subscription_price)
        } else {
          this.session_info.payment.selected_subscription_price = null
          this.coreService.setSessionData(this.session_info, false)
        }
      }
    }
  }

  openModal(id: string, price: LocalSubPricing = null) {
    this.modal_element = new bootstrap.Modal(document.getElementById(id), {
      keyboard: false
    })
    if (price) this.selected_local_price = JSON.parse(JSON.stringify(price))
    this.modal_element.show()
  }

  interpretPricing() {
    this.local_prices = this.prices.filter(p => p.plan.durations.in === this.duration).map(price => {
      const highlights: { v: string; k: string; }[] = []
      highlights.push({
        v: price.plan?.unlimited_scans ? 'Unlimited' : price.plan.scans.toLocaleString(),
        k: price.plan?.unlimited_scans ? `QR Scans` : `Monthly QR Scans`
      })
      highlights.push({
        v: `Unlimited`,
        k: `Static QR Codes`
      })
      highlights.push({
        v: price.plan.max_dynamic_qrs.toLocaleString(),
        k: `Dynamic QR Codes`
      })
      highlights.push({
        v: price.plan.workspaces.toLocaleString(),
        k: this.functionService.pluralize('Workspace', price.plan.workspaces)
      })
      highlights.push({
        v: price.plan.user_seats.toLocaleString(),
        k: `User ${this.functionService.pluralize('seat', price.plan.user_seats)}`
      })
      highlights.push({
        v: price.plan.roles.includes(`${SystemUserTypes.USER}.USERMANAGEMENT.MANAGEPOPUPS`) ? "<i class='bi bi-check2-all text-success'></i>" : "<i class='bi bi-x-lg text-danger'></i>",
        k: "Pop-up Form"
      })
      if (price.plan.api_keys) {
        highlights.push({
          v: price.plan.api_keys.request_monthly_limit.toLocaleString(),
          k: `API monthly requests / key`
        })
      }
      return {
        highlights,
        id: price.id,
        title: price.title,
        rank: price.plan.rank,
        currency: price.currency,
        unit_price: price.unit_price,
        is_popular: price.is_popular,
        product_code: price.product_code,
        billed: price.plan.durations.in === ProductSubscriptionDurations.DAY ? `daily` : `${price.plan.durations.in}ly`,
        show_price: price.product_code === ProductCodes.QR_PLAN_BASIC || price.product_code === ProductCodes.QR_PLAN_LITE || price.product_code === ProductCodes.QR_PLAN_PRO,
      }
    })
  }

  private async getPricings() {
    let url = this.config.apiEndpoints.resources.pricings
    if (this.coreService.isLoggedIn()) {
      url = this.session_info.user_details.user_category === SystemUserTypes.ADMIN ? this.config.apiEndpoints.user.pricings.replace("/{id}", `/${this.user_details.id}`) : this.config.apiEndpoints.user.pricings.replace("/{id}", ``)
      url = `${url}plan_subscription`
    }
    this.store.dispatch(AppActions.fetchSubscriptionPrices({ url, force: true }))
  }

  async getStarted(price: LocalSubPricing) {

    if (!price.show_price) {
      this.router.navigateByUrl("/contact-us?reason=0")
      return
    }

    if (!this.coreService.isLoggedIn()) {
      // Start login process
      this.session_info.payment.selected_subscription_price = price
      this.session_info.last_routes.lastvisited = this.router.url
      this.coreService.setSessionData(this.session_info)
      this.router.navigateByUrl(`/auth/signup`)
      return
    }

    this.session_info.payment.selected_subscription_price = null
    this.coreService.setSessionData(this.session_info, false)

    if (this.busy_ids.includes(price.id)) return

    if (!this.user_details.current_organization_details.subscription_details.subscription.price) {
      return this.paymentService.startPayment(this.prices.find(p => p.id === price.id))
    }
    // On a paid subscription
    // Prorate or downgrade
    let all_good = true
    this.busy_ids.push(price.id)
    try {

      const url = this.session_info.user_details.user_category === SystemUserTypes.ADMIN ? this.config.apiEndpoints.user.payments.manage_subscription.replace("/{id}", `/${this.user_details.id}`) : this.config.apiEndpoints.user.payments.manage_subscription.replace("/{id}", ``)
      const response: SubscriptionPriceOnCheckout = (await this.coreService.getRequest(`${url}subscription-checkout-preflight/${price.id}`) as any)?.checkout_preflight_result
      this.session_info.payment.order_details = this.paymentService.initOrderDetails()

      this.busy_ids = this.busy_ids.filter(bi => bi !== price.id)

      this.session_info.payment.order_details.plan_subscription_check_result = response
      all_good = this.session_info.payment.order_details.plan_subscription_check_result.action !== SubscriptionPriceOnCheckoutActions.FORBIDDEN
      if (this.session_info.payment.order_details.plan_subscription_check_result.action === SubscriptionPriceOnCheckoutActions.DOWNGRADE) {
        // downgrade subscription instead
        this.openModal('downgradePlanModal', price)
        return
      }
      if (this.session_info.payment.order_details.plan_subscription_check_result.action === SubscriptionPriceOnCheckoutActions.UPGRADE) {
        this.coreService.setSessionData(this.session_info)
        return this.paymentService.startPayment(this.prices.find(p => p.id === price.id), false)
      }
    } catch (error) {
      all_good = false
    }
    if (!all_good) {
      this.openModal('checkoutNotAllowed', price)
      return
    }

  }

  async downgradeSubscription() {
    if (!this.downgrading) {
      this.downgrading = true
      try {

        const url = this.session_info.user_details.user_category === SystemUserTypes.ADMIN ? this.config.apiEndpoints.user.payments.manage_subscription.replace("/{id}", `/${this.user_details.id}`) : this.config.apiEndpoints.user.payments.manage_subscription.replace("/{id}", ``)
        await this.coreService.getRequest(`${url}change-upcoming-subscription/${this.selected_local_price.id}`)
        this.user_details.current_organization_details.subscription_details.autorenew = true
        this.user_details.current_organization_details.subscription_details.cancelled_at = ""
        this.modal_element?.hide()
        this.functionService.showSuccessMessage("Your subscription will be downgraded on " + this.functionService.datePimped(this.user_details.current_organization_details.subscription_details.end_date, false))
        if (this.user_details.id === this.session_info.user_details.id) {
          this.session_info.user_details = this.user_details
          this.coreService.setSessionData(this.session_info, false)
          this.coreService.refreshSession()
          this.coreService.gotoAccountSettings()
        }
      } catch (error) {
      }
    }
    this.downgrading = false
  }

}

